import Icon from '@atoms/Icon/Icon';
import { IUSPs } from '@models/IUSPs';
import NotificationCarousel from '@molecules/NotificationCarousel/NotificationCarousel';
import useComponentsHeightsStore from '@state/ComponentsHeightsStore';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import useIsTouchDevice from '@utils/hooks/useIsTouchDevice';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { SwiperSlide } from 'swiper/react';
import USPsFitStrip from './USPsFitStrip';

const USPsStrip = ({ items }: IUSPs) => {
  const isMobile = useMediaQuery(1024);
  const isTouchDevice = useIsTouchDevice();
  const [
    stripWrapperRef,
    { width: stripWrapperWidth, height: stripWrapperHeight },
  ] = useResizeObserver();
  const { setUSPsStripHeight } = useComponentsHeightsStore((state) => ({
    setUSPsStripHeight: state.setUSPsStripHeight,
  }));
  const { uspStripAriaLabel } = useAriaLabels();
  const [fitStripRef, { scrollWidth: fitStripScrollWidth }] =
    useResizeObserver();
  const [isStripContentOverflowing, setIsStripContentOverflowing] =
    useState<boolean>();
  const isStripLoading =
    isStripContentOverflowing === undefined || isTouchDevice === undefined;

  useEffect(() => {
    setIsStripContentOverflowing(
      stripWrapperWidth > 0 &&
        fitStripScrollWidth > Math.ceil(stripWrapperWidth) - 60
    );
  }, [stripWrapperWidth, fitStripScrollWidth]);

  useEffect(() => {
    setUSPsStripHeight(stripWrapperHeight);
  }, [stripWrapperHeight, setUSPsStripHeight]);

  return (
    <div
      className="relative z-100 bg-grey-600 text-white"
      ref={stripWrapperRef}
      data-testid="CT-141-USPs-Strip"
      role="region"
      aria-label={uspStripAriaLabel}
    >
      {isStripContentOverflowing || isTouchDevice ? (
        <NotificationCarousel
          id="USPs-strip-carousel"
          hasMultipleItems={items.length > 1}
          enabledNavigationArrowColor="text-white"
        >
          {items.map((item, index) => (
            <SwiperSlide className="stretch-card" key={item.title}>
              <div
                className="flex h-full items-center justify-center"
                data-testid="CT-141-USPs-Strip-Wrapper"
              >
                <a
                  href={item.link?.url}
                  target={item.link?.target}
                  className={classNames(
                    'flex h-full w-full items-center justify-center px-8 py-2 xl:px-[4.625rem]',
                    {
                      'pointer-events-none': !item.link?.url,
                      'hover:underline': !isTouchDevice,
                    }
                  )}
                  data-testid={`CT-141-USPs-Strip-Item-${index}`}
                >
                  <div className="flex gap-x-2 lg:gap-x-2.5">
                    <span className="flex h-5 items-center lg:h-6">
                      <Icon
                        name={item.icon}
                        size="1.25rem"
                        colorClassName="text-white"
                        className="shrink-0"
                      />
                    </span>

                    <p
                      className={`flex items-center text-center ${
                        isMobile ? 'body-four' : 'body-three'
                      }`}
                      data-testid={`CT-141-USPs-Strip-Item-Title-${index}`}
                    >
                      {item.title}
                    </p>
                  </div>
                </a>
              </div>
            </SwiperSlide>
          ))}
        </NotificationCarousel>
      ) : (
        <USPsFitStrip isStripLoading={isStripLoading} items={items} />
      )}

      {/* Duplicate the strip fit version within an invisible wrapper in order to always be able to properly 
      compute the moment for switching between the fit version and the carousel version on window resizing */}
      <div className="invisible absolute left-0 right-0 top-0 overflow-hidden">
        <USPsFitStrip
          isStripLoading={isStripLoading}
          items={items}
          ref={fitStripRef}
        />
      </div>
    </div>
  );
};

export default USPsStrip;
